import { useState } from 'react';
import {
  Award,
  BookOpen,
  Clock,
  Zap,
  Flame,
  ChevronRight,
  ChevronDown,
  ShieldCheck,
  Calendar,
  Sparkles,
  Layers,
  ArrowRight,
  Brain,
  Mail,
  X,
  CheckCircle2,
  AlertCircle,
  FileText,
  Sliders,
  Compass
} from 'lucide-react';
import type { CertificationWithDetails } from '@shared/types';

interface DashboardViewProps {
  certifications: CertificationWithDetails[];
  selectedCertId: string;
  onSelectCertId: (id: string) => void;
  onStartExam: (mode: 'study' | 'timed_exam', questionCount: number, domainFilter?: string[]) => void;
  onStartDailyChallenge: () => void;
  dueReviewCount?: number;
  onStartSpacedReview?: () => void;
  onSimulateCron?: () => void;
}

// BOK Competency Mapping derived from official ASQ and RAPS syllabi
const BOK_COMPETENCIES: Record<string, { title: string; competencies: string[] }> = {
  'cpgp-dom-1': {
    title: 'Regulatory Agency Governance & Statutory Frameworks',
    competencies: [
      'FDA Form 483 15-business-day response window & CPGM 7356.002',
      'Warning Letters, Consent Decrees & Import Alert 99-32 (Refusal of Inspection)',
      'Quality Technical Agreements (21 CFR 211.22 vs EU Chapter 7)',
      'Statutory drug adulteration under FD&C Act Section 501(a)(2)(B)',
      'Combination products streamlined quality system under 21 CFR Part 4',
      'EU Regulation 536/2014 & Annex 13 two-step Qualified Person (QP) release',
      'PIC/S & Mutual Recognition Agreements (MRA) operational impact'
    ]
  },
  'cpgp-dom-2': {
    title: 'Quality Management Systems, Deviation & CAPA Architecture',
    competencies: [
      'ICH Q10 Pharmaceutical Quality System & Senior Management Review',
      'CAPA root cause analysis (5-Whys, Fishbone, FMEA) avoiding "human error" traps',
      '21 CFR Part 7 Recall Classifications: Class I (fatal/serious), Class II, Class III',
      'Deviation investigation extension procedures (>30 days) and interim risk reviews',
      'EU GMP Chapter 1 Product Quality Review (PQR) vs 21 CFR 211.180(e) APR',
      'Change control categorization (Major PAS vs Moderate CBE-30 vs Minor AR)',
      'Verification of Effectiveness (VoE) statistical sample size determination'
    ]
  },
  'cpgp-dom-3': {
    title: 'Laboratory Controls, OOS Investigations & Analytical Lifecycle',
    competencies: [
      'FDA OOS Guidance: Phase I Laboratory Investigation vs Phase II Manufacturing Investigation',
      'Manual chromatographic peak integration SOP controls & QA approval mandate',
      'USP <621> System Suitability Testing (SST) bracketing failure invalidate criteria',
      'ICH Q1A(R2) Stability testing "significant change" thresholds (5% potency drop)',
      'USP <1224> Analytical method transfer comparative testing & equivalence protocols',
      'ICH Q2(R2) Method validation metrics (LOD 3:1 S/N vs LOQ 10:1 S/N, intermediate precision)'
    ]
  },
  'cpgp-dom-4': {
    title: 'Sterile Manufacturing, Annex 1 (2022) & Contamination Control',
    competencies: [
      'EU GMP Annex 1 (2022) comprehensive Contamination Control Strategy (CCS)',
      'Pre-Use Post-Sterilization Integrity Testing (PUPSIT) risk assessment & Section 8.87',
      'Cleanroom airborne particulate classification (ISO 14644-1) vs continuous monitoring',
      'Water for Injection (WFI) statutory limits (<10 CFU/100 mL, <0.25 EU/mL endotoxin)',
      'Aseptic Process Simulation (APS / Media Fill) zero-growth criteria & failure actions',
      'Annex 1 Table 6 personnel exit gowning microbiological monitoring thresholds'
    ]
  },
  'cpgp-dom-5': {
    title: 'Validation Lifecycle, GAMP 5 & Part 11 Data Integrity',
    competencies: [
      'FDA 2011 3-Stage Process Validation Lifecycle: Stage 1 Design, Stage 2 PPQ, Stage 3 CPV',
      'ICH Q8(R2) Quality by Design (QbD) Design Space flexibility & control strategy',
      '21 CFR Part 11 electronic records, electronic signatures, and ALCOA+ principles',
      'Audit trail review SOP frequencies for critical batch release parameters',
      'GAMP 5 Category 4 configured software lifecycle qualification (URS to PQ)',
      'USP <1079> Cold chain temperature excursions & Mean Kinetic Temperature (MKT) rules'
    ]
  },
  'rac-dom-1': {
    title: 'Strategic Development Pathways & Expedited Regulatory Programs',
    competencies: [
      'Breakthrough Therapy Designation (BTD) under FD&C Act Section 506(a)',
      'Accelerated Approval (surrogate endpoint) & FDORA 2022 expedited withdrawal authority',
      'Orphan Drug Act: <200,000 prevalence criteria & 7-year statutory marketing exclusivity',
      'PDUFA VII formal meeting types (Type A 30-day clock for clinical holds, Type B, Type C)',
      'PREA mandatory pediatric assessment vs BPCA 6-month voluntary pediatric exclusivity',
      'Target Product Profile (TPP) strategic design from preclinical to label claims',
      'EMA PRIME (Priority Medicines) and Accelerated Assessment (150-day clock)'
    ]
  },
  'rac-dom-2': {
    title: 'Clinical Development, IND Operations & Safety Reporting',
    competencies: [
      '21 CFR 312.40 30-day statutory IND review clock & FDA Clinical Hold grounds',
      'IND Safety Reporting (21 CFR 312.32): 15-day SUSAR vs 7-day fatal/life-threatening alert',
      'Special Protocol Assessment (SPA) binding agreements under FD&C Act 505(b)(4)(B)',
      'Investigator Brochure (IB) mandatory annual review under 21 CFR 312.55 & ICH E6(R2)',
      '21 CFR 50.24 Exception from Informed Consent (EFIC) for emergency research protocols',
      'Decentralized Clinical Trials (DCT) and eConsent compliance under 21 CFR Part 11/50'
    ]
  },
  'rac-dom-3': {
    title: 'Marketing Applications, Review Procedures & Dossier Architecture',
    competencies: [
      '505(b)(1) NDA vs 505(b)(2) hybrid NDA vs 505(j) ANDA, Paragraph IV & 30-month stay',
      'ICH M4 Common Technical Document (CTD) 5-Module architecture & Module 3 CMC structure',
      'BPCIA Section 351(k) biosimilars & 12-year reference biologic exclusivity',
      'PDUFA VII review timeframes: 10-month Standard vs 6-month Priority review clocks',
      'FDA Advisory Committee (AC) meetings & non-binding recommendation status (Part 14)',
      'Physician Labeling Rule (PLR) format & Highlights section under 21 CFR 201.56/57',
      'EU Centralised Procedure mandatory scope under Regulation (EC) No 726/2004'
    ]
  },
  'rac-dom-4': {
    title: 'Post-Approval Lifecycle Management & Global Pharmacovigilance',
    competencies: [
      '21 CFR 314.70 post-approval CMC changes: PAS (4-month) vs CBE-30 vs Annual Report',
      'Post-marketing 15-day alert reports (ICSR) under 21 CFR 314.80 for unexpected serious ADEs',
      'Field Alert Reports (FAR) 3-working-day mandatory notification under 21 CFR 314.81(b)(1)',
      'Risk Evaluation and Mitigation Strategies (REMS) & Elements to Assure Safe Use (ETASU)',
      'Form FDA 2253 initial dissemination submission for promotional labeling (21 CFR 314.81)',
      'NDA Annual Reports 60-day anniversary submission clock & post-marketing commitments (PMC)'
    ]
  }
};

export function DashboardView({
  certifications,
  selectedCertId,
  onSelectCertId,
  onStartExam,
  onStartDailyChallenge,
  dueReviewCount = 0,
  onStartSpacedReview,
  onSimulateCron
}: DashboardViewProps) {
  const currentCert = certifications.find(c => c.id === selectedCertId) || certifications[0];
  const isCpgp = currentCert?.id === 'asq-cpgp';

  // Modal and accordion state
  const [isOpenBookModalOpen, setIsOpenBookModalOpen] = useState(false);
  const [expandedDomainId, setExpandedDomainId] = useState<string | null>(null);
  const [selectedTimedCount, setSelectedTimedCount] = useState<number>(isCpgp ? 165 : 100);

  // Timed simulation presets
  const timedPresets = isCpgp
    ? [
        { count: 165, label: 'Prometric Full Mock', duration: '258 min (4h 18m)', desc: 'Exact ASQ Prometric conditions (150 scored + 15 unscored pre-test questions)' },
        { count: 50, label: 'Half Mock', duration: '78 min', desc: 'Balanced distribution across all 5 BoK domains' },
        { count: 25, label: 'Domain Sprint', duration: '39 min', desc: 'High-intensity scenario diagnostic' },
        { count: 10, label: 'Rapid Check', duration: '15 min', desc: 'Quick 10-question timed assessment' }
      ]
    : [
        { count: 100, label: 'Full RAC Mock', duration: '150 min (2.5h)', desc: 'Standard RAPS exam simulation covering all 4 domains' },
        { count: 50, label: 'Half Mock', duration: '75 min', desc: 'Balanced scenario distribution across IND/NDA/Lifecycle' },
        { count: 25, label: 'Sprint', duration: '38 min', desc: 'Rapid regulatory decision diagnostic' },
        { count: 10, label: 'Rapid Check', duration: '15 min', desc: 'Quick 10-question timed assessment' }
      ];

  const toggleDomain = (domainId: string) => {
    setExpandedDomainId(prev => (prev === domainId ? null : domainId));
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Hero Welcome & Streak Banner */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-50/80 via-white to-cyan-50/60 dark:from-slate-900 dark:via-indigo-950 dark:to-slate-900 border border-slate-200/80 dark:border-slate-800 p-6 sm:p-8 shadow-sm dark:shadow-2xl transition-colors duration-200">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-700 dark:text-indigo-400 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              <span>GXP Compliance & Certification Simulator</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
              Master Life Sciences Regulatory Standards
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 max-w-2xl leading-relaxed">
              Targeted scenario drills for ASQ CPGP and RAPS RAC Drugs directly aligned with Title 21 CFR, EU GMP EudraLex Volume 4, Annex 1 (2022), and ICH Guidelines.
            </p>
          </div>

          {/* User Metrics & Open-Book Guide Pill */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center space-x-4 bg-white/90 dark:bg-slate-950/70 border border-slate-200 dark:border-slate-800/80 rounded-2xl p-4 backdrop-blur shadow-sm">
              <div className="flex items-center space-x-2.5 pr-4 border-r border-slate-200 dark:border-slate-800">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <Flame className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-lg font-bold text-slate-900 dark:text-slate-100">7 Days</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">Study Streak</div>
                </div>
              </div>

              <div className="flex items-center space-x-2.5 pl-1">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <ShieldCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-lg font-bold text-slate-900 dark:text-slate-100">Full Coverage</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">Official BoK</div>
                </div>
              </div>
            </div>

            {/* Open Book Binder Guide Trigger */}
            <button
              onClick={() => setIsOpenBookModalOpen(true)}
              className="px-4 py-3 rounded-2xl bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30 text-xs font-bold transition flex items-center gap-2 shadow-xs"
            >
              <Compass className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Exam Day & Binder Guide</span>
            </button>
          </div>
        </div>
      </div>

      {/* Certification Track Switcher */}
      <div className="space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <Layers className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> Select Examination Track
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {certifications.map(cert => {
            const isSelected = cert.id === selectedCertId;
            return (
              <button
                key={cert.id}
                onClick={() => {
                  onSelectCertId(cert.id);
                  setSelectedTimedCount(cert.id === 'asq-cpgp' ? 165 : 100);
                }}
                className={`text-left p-5 rounded-2xl border transition-all relative overflow-hidden shadow-sm ${
                  isSelected
                    ? 'bg-white dark:bg-slate-900 border-indigo-500/80 dark:border-indigo-500/60 shadow-md shadow-indigo-500/10 ring-1 ring-indigo-500/30'
                    : 'bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/70'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-slate-900 dark:text-slate-100 text-base">{cert.title}</span>
                      {isSelected && (
                        <span className="bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2 leading-relaxed">{cert.description}</p>
                  </div>
                  <div className={`p-2 rounded-xl border ${
                    isSelected
                      ? 'bg-indigo-500/15 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 border-indigo-500/40'
                      : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'
                  }`}>
                    <Award className="w-5 h-5" />
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-mono">
                  <span>{cert.domains?.length || 5} BOK Domains</span>
                  <span className="text-indigo-600 dark:text-indigo-400 font-semibold">Full BoK Coverage</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick Action Cards Grid */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500 dark:text-amber-400" /> Start Practice & Retention Drills
          </h2>
          {onSimulateCron && (
            <button
              onClick={onSimulateCron}
              className="text-[11px] font-mono text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-300 flex items-center gap-1.5 transition bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-indigo-400 dark:hover:border-indigo-500/50 px-2.5 py-1 rounded-lg shadow-sm"
              title="Dispatch Daily Micro-Challenge Email"
            >
              <Mail className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
              <span>Test Daily Challenge Email</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Action 1: Timed Exam Simulation with Prometric Presets */}
          <div className="bg-white dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-950 border border-slate-200 dark:border-slate-800 hover:border-indigo-400 dark:hover:border-indigo-500/40 rounded-2xl p-6 transition flex flex-col justify-between group shadow-sm hover:shadow-md dark:shadow-lg relative overflow-hidden">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 group-hover:scale-105 transition">
                  <Clock className="w-6 h-6" />
                </div>
                <span className="text-[10px] font-mono font-bold bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded border border-indigo-500/30">
                  PROMETRIC CBT
                </span>
              </div>
              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Full Timed Simulation</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Realistic exam conditions with strike-through distractor elimination and question navigation review grid.
              </p>

              {/* Preset Selector */}
              <div className="pt-2 space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  <Sliders className="w-3 h-3" /> Select Length:
                </label>
                <div className="grid grid-cols-2 gap-1.5">
                  {timedPresets.map(preset => (
                    <button
                      key={preset.count}
                      onClick={() => setSelectedTimedCount(preset.count)}
                      className={`text-left p-2 rounded-lg border text-xs transition ${
                        selectedTimedCount === preset.count
                          ? 'bg-indigo-600 text-white border-indigo-600 font-bold shadow-xs'
                          : 'bg-slate-50 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                      }`}
                    >
                      <div className="font-mono text-[11px]">{preset.count} Qs</div>
                      <div className="text-[9px] opacity-80">{preset.duration.split(' ')[0]} {preset.duration.split(' ')[1]}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-5">
              <button
                onClick={() => onStartExam('timed_exam', selectedTimedCount)}
                className="w-full py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs transition shadow-md shadow-indigo-600/20 dark:shadow-indigo-950 flex items-center justify-center space-x-2"
              >
                <span>Launch Timed ({selectedTimedCount} Qs)</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Action 2: Quick Study Drill */}
          <div className="bg-white dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-950 border border-slate-200 dark:border-slate-800 hover:border-emerald-400 dark:hover:border-emerald-500/40 rounded-2xl p-6 transition flex flex-col justify-between group shadow-sm hover:shadow-md dark:shadow-lg">
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 group-hover:scale-105 transition">
                <BookOpen className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Study Mode Drill</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Untimed study mode with instant feedback. Inspect detailed option rationales and statutory citations after each selection.
              </p>
            </div>
            <div className="pt-6">
              <button
                onClick={() => onStartExam('study', 10)}
                className="w-full py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs transition shadow-md shadow-emerald-600/20 dark:shadow-emerald-950 flex items-center justify-center space-x-2"
              >
                <span>Start Study Drill (10 Qs)</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Action 3: Spaced Repetition Due Review */}
          <div className="bg-white dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-950 border border-slate-200 dark:border-slate-800 hover:border-purple-400 dark:hover:border-purple-500/40 rounded-2xl p-6 transition flex flex-col justify-between group shadow-sm hover:shadow-md dark:shadow-lg">
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 flex items-center justify-center text-purple-600 dark:text-purple-400 group-hover:scale-105 transition">
                <Brain className="w-6 h-6" />
              </div>
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Spaced Review</h3>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${
                  dueReviewCount > 0
                    ? 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30 dark:border-purple-500/40'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                }`}>
                  SM-2
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Re-drill missed items on an SM-2 optimal decay curve to cement long-term regulatory retention.
              </p>
            </div>
            <div className="pt-6">
              <button
                onClick={onStartSpacedReview}
                disabled={!onStartSpacedReview}
                className="w-full py-2.5 px-4 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white font-semibold text-xs transition shadow-md shadow-purple-600/20 dark:shadow-purple-950 flex items-center justify-center space-x-2"
              >
                <span>Review Due ({dueReviewCount})</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Action 4: Daily Challenge */}
          <div className="bg-white dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-950 border border-slate-200 dark:border-slate-800 hover:border-amber-400 dark:hover:border-amber-500/40 rounded-2xl p-6 transition flex flex-col justify-between group shadow-sm hover:shadow-md dark:shadow-lg">
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 flex items-center justify-center text-amber-600 dark:text-amber-400 group-hover:scale-105 transition">
                <Calendar className="w-6 h-6" />
              </div>
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Daily Challenge</h3>
                <span className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 text-[9px] font-mono px-1.5 py-0.5 rounded">
                  DAILY
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                One high-impact scenario benchmark refreshed daily at UTC midnight. Maintain your study streak.
              </p>
            </div>
            <div className="pt-6">
              <button
                onClick={onStartDailyChallenge}
                className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 text-white font-semibold text-xs transition shadow-md shadow-amber-600/20 dark:shadow-amber-950 flex items-center justify-center space-x-2"
              >
                <span>Take Daily (1 Q)</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Body of Knowledge Mastery & Syllabus Breakdown */}
      <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-sm dark:shadow-xl space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-slate-100 dark:border-slate-800 pb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <span>Body of Knowledge (BOK) Domains</span>
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Official syllabus weightings, competency domains, and regulatory subtopics for {currentCert?.title}
            </p>
          </div>
          <div className="text-xs text-slate-400 dark:text-slate-500 font-mono">
            {currentCert?.domains?.length || 5} Domains Active
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentCert?.domains?.map(domain => {
            const isExpanded = expandedDomainId === domain.id;
            const competencyData = BOK_COMPETENCIES[domain.code] || BOK_COMPETENCIES[domain.id];

            return (
              <div
                key={domain.id}
                className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:border-slate-300 dark:hover:border-slate-700 transition space-y-4 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400">{domain.code}</span>
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5 block">{domain.name}</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-100 bg-white dark:bg-slate-800 px-2.5 py-1 rounded-md border border-slate-200 dark:border-slate-700 shadow-xs">
                    {domain.weight_percentage}%
                  </span>
                </div>

                {/* Progress / Weight visual bar */}
                <div className="space-y-1.5">
                  <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-indigo-500 via-indigo-400 to-emerald-400"
                      style={{ width: `${Math.min(100, domain.weight_percentage * 3.5)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between items-center text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                    <span>Official BoK Weight</span>
                    <button
                      onClick={() => onStartExam('study', 10, [domain.id])}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-sans font-semibold flex items-center gap-1 hover:underline"
                    >
                      <span>Practice Domain</span>
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Subtopic Competencies Accordion */}
                {competencyData && (
                  <div className="pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
                    <button
                      onClick={() => toggleDomain(domain.id)}
                      className="w-full flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium py-1 transition"
                    >
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3 text-indigo-500" />
                        <span>Syllabus Sub-Topics ({competencyData.competencies.length})</span>
                      </span>
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5 rotate-180 transition-transform" /> : <ChevronDown className="w-3.5 h-3.5 transition-transform" />}
                    </button>

                    {isExpanded && (
                      <div className="mt-2 pl-2 space-y-1.5 text-[11px] text-slate-600 dark:text-slate-400 animate-fadeIn">
                        {competencyData.competencies.map((c, i) => (
                          <div key={i} className="flex items-start gap-1.5">
                            <span className="text-indigo-500 font-bold">•</span>
                            <span className="leading-snug">{c}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Prometric Exam Day & Open-Book Binder Guide Modal */}
      {isOpenBookModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6 sm:p-8 space-y-6">
            <div className="flex items-start justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 rounded-2xl bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/30">
                  <Compass className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                    Official ASQ Exam Day & Open-Book Binder Guide
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Prometric test-center regulations, permitted reference materials, and pacing strategies
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpenBookModalOpen(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Section 1: The Open Book Rule */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-slate-100">
                <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>What Can You Bring Into Prometric?</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                The ASQ CPGP is an <strong>open-book examination</strong>. However, Prometric test proctors enforce strict binding standards. Candidates who bring improper materials are barred from using them during the exam.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                <div className="p-4 rounded-2xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800 dark:text-emerald-300">
                    <CheckCircle2 className="w-4 h-4" /> Permitted in the Testing Room:
                  </div>
                  <ul className="text-xs text-emerald-900/90 dark:text-emerald-200/90 space-y-1.5 list-disc list-inside">
                    <li><strong>The Certified Pharmaceutical GMP Professional Handbook</strong> (Durivage / ASQ Quality Press)</li>
                    <li>Clean, printed copies of <strong>21 CFR Parts 11, 210, 211, 820</strong> and <strong>EU GMP EudraLex Vol 4 & Annex 1</strong></li>
                    <li>Reference books with glued bindings or spiral/comb/ring binders</li>
                    <li>Permanent index tabs attached to pages (e.g. labeled tabs for CFR parts)</li>
                  </ul>
                </div>

                <div className="p-4 rounded-2xl bg-rose-50/70 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-rose-800 dark:text-rose-300">
                    <AlertCircle className="w-4 h-4" /> Strictly Prohibited (Disqualification Risk):
                  </div>
                  <ul className="text-xs text-rose-900/90 dark:text-rose-200/90 space-y-1.5 list-disc list-inside">
                    <li><strong>Loose papers or unbound sheets</strong> of any kind (must be bound)</li>
                    <li>Sticky notes or post-it flags with <strong>handwritten text or notes</strong></li>
                    <li>Smartphones, smartwatches, or tablets (must be stored in lockers)</li>
                    <li>Calculators with full QWERTY keyboards or programmable text memory</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 2: Prometric Test-Center Pacing Strategy */}
            <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-slate-100">
                <Clock className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>Pacing & Time Management (The 1.56 Minute Rule)</span>
              </div>
              <div className="p-4 rounded-2xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 text-xs text-slate-700 dark:text-slate-300 leading-relaxed space-y-2">
                <p>
                  <strong>Total Time:</strong> 4 hours and 18 minutes (258 minutes) for 165 multiple-choice questions (150 scored, 15 pre-test unscored). This provides an average of <strong>1.56 minutes per question</strong>.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="bg-white/80 dark:bg-slate-900/80 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                    <strong className="text-indigo-600 dark:text-indigo-400 block mb-1">Pass 1: Velocity Drill</strong>
                    Answer all questions where the principle is clear. Never consult your open-book during Pass 1. Flag scenario questions that require technical cross-checking.
                  </div>
                  <div className="bg-white/80 dark:bg-slate-900/80 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                    <strong className="text-indigo-600 dark:text-indigo-400 block mb-1">Pass 2: Targeted Index Lookup</strong>
                    Use your binder index exclusively to look up exact statutory thresholds (e.g. WFI bioburden limits, stability change percentages, 15-day alert criteria).
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: ASQ Scaled Score Benchmark */}
            <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-slate-100">
                <Award className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span>ASQ Scaled Scoring (550 Cutoff)</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                ASQ converts candidate raw scores into a scaled score ranging from <strong>100 to 750</strong>. The required passing score across all exam forms is <strong>550</strong> (approximately 70%–75% raw correct). Our exam report calculates your estimated ASQ scaled score after every simulation.
              </p>
            </div>

            <div className="pt-4 flex justify-end border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => setIsOpenBookModalOpen(false)}
                className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition shadow-sm"
              >
                Understood, Close Guide
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
